import React, { useContext } from 'react';
import Isvg from 'react-inlinesvg';
import PropTypes from 'prop-types';
import _isFunction from 'lodash/isFunction';
import _isString from 'lodash/isString';
import _noop from 'lodash/noop';

import When from 'components/When';
import { intlMessage } from 'constants/propTypes';
import { isFragment } from 'helpers/core';
import { bem } from 'helpers/class-name';
import Message from 'components/Message';
import { SvgArrow } from 'components/Svgs';
import { DropdownContext } from 'features/App/Contexts';

import './DropdownToggle.scss';

const { block, element } = bem('dropdown-toggle2');

function DropdownToggle(props) {
  const { toggleId } = useContext(DropdownContext);

  const {
    className,
    label,
    isActive,
    hasError,
    danger,
    hideCaret,
    children,
    disabled,
    isOpen,
    toggleMenu,
    icon,
    startIcon,
    isAutocomplete,
    isAllSelected,
    isNoneSelected,
    isRoundRadiusStyle,
  } = props;

  let theme = 'light';
  let arrowTheme = 'dark';
  if (disabled) {
    theme = 'disabled';
    arrowTheme = 'disabled';
  } else if (danger) {
    theme = 'danger';
    arrowTheme = 'danger';
  } else if (hasError) {
    theme = 'warn';
    arrowTheme = 'warn';
  } else if (isActive) {
    theme = 'brand';
    arrowTheme = 'brand';
  }
  const themeMod = { [`theme-${theme}`]: true };

  // normalize the direct-children and renderProp-children.
  let renderPropChildren = _isString(children)
    ? React.createElement('span', {}, children) // ensure a wrapper for ellipsification
    : children;
  if (_isFunction(children)) {
    [renderPropChildren] = React.Children.toArray(
      children({
        isOpen,
        disabled,
        toggleMenu,
        isAllSelected,
        isNoneSelected,
      })
    );
    renderPropChildren = isFragment(renderPropChildren)
      ? React.Children.toArray(renderPropChildren.props.children)
      : [renderPropChildren];
  }

  return (
    <div
      id={toggleId}
      className={block({ ...themeMod, isAutocomplete, isRoundRadiusStyle }, className)}
      onClick={toggleMenu}
      /* these appease jsx-a11y, everything is handled in the parent Dropdown */
      role="button"
      onKeyDown={_noop}
      tabIndex="-1">
      {startIcon && <Isvg className={element('start-icon')} src={startIcon} />}

      {label && <Message className={element('label', themeMod)} message={label} />}

      <div className={element('content')}>{renderPropChildren}</div>

      <When predicate={!hideCaret}>
        <span className={element('caret')}>
          {!icon ? <SvgArrow direction={isOpen ? 'up' : 'down'} theme={arrowTheme} /> : <Isvg src={icon} />}
        </span>
      </When>
    </div>
  );
}

DropdownToggle.defaultProps = {
  danger: false,
  disabled: false,
  hasError: false,
  hideCaret: false,
  isActive: false,
  isOpen: false,
  label: null,
  toggleMenu: () => {},
};

DropdownToggle.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  className: PropTypes.string,
  danger: PropTypes.bool,
  disabled: PropTypes.bool,
  hasError: PropTypes.bool,
  hideCaret: PropTypes.bool,
  icon: PropTypes.string,
  startIcon: PropTypes.string,
  isActive: PropTypes.bool,
  isAllSelected: PropTypes.bool,
  isAutocomplete: PropTypes.bool,
  isNoneSelected: PropTypes.bool,
  isOpen: PropTypes.bool,
  isRoundRadiusStyle: PropTypes.bool,
  label: PropTypes.oneOfType([intlMessage, PropTypes.oneOf([false])]),
  toggleMenu: PropTypes.func,
};

export default DropdownToggle;
